import { Node, mergeAttributes } from '@tiptap/core';
import type { Editor } from '@tiptap/react';

export const MergeToken = Node.create({
  name: 'mergeToken',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: (element) =>
          element.getAttribute('data-merge-token') || element.getAttribute('data-variable') || '',
        renderHTML: (attributes) => ({
          'data-merge-token': attributes.name,
          'data-variable': attributes.name,
        }),
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'span[data-merge-token]' },
      { tag: 'span[data-variable]' },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const varName = node.attrs.name || HTMLAttributes.name || '';
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-merge-token': varName,
        'data-variable': varName,
        class:
          'inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded-md bg-purple-100 text-purple-700 text-xs font-mono font-bold border border-purple-200 select-none align-baseline',
        contenteditable: 'false',
      }),
      `{{${varName}}}`,
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement('span');
      dom.className =
        'inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded-md bg-purple-100 text-purple-700 text-xs font-mono font-bold border border-purple-200 select-none align-baseline';
      const varName = node.attrs.name || '';
      dom.setAttribute('data-merge-token', varName);
      dom.setAttribute('data-variable', varName);
      dom.setAttribute('contenteditable', 'false');
      dom.textContent = `{{${varName}}}`;

      dom.onmousedown = (e) => {
        e.preventDefault();
        const pos = getPos();
        if (typeof pos === 'number') {
          editor.commands.selectNode(pos);
        }
        return false;
      };

      return {
        dom,
        contentDOM: null,
        update: (updatedNode) => {
          if (updatedNode.attrs.name !== node.attrs.name) {
            const newName = updatedNode.attrs.name || '';
            dom.setAttribute('data-merge-token', newName);
            dom.setAttribute('data-variable', newName);
            dom.textContent = `{{${newName}}}`;
            return true;
          }
          return false;
        },
      };
    };
  },
});

/**
 * Reusable helper: inserts or replaces selected content with a mergeToken in a Tiptap editor
 */
export function insertMergeTokenToEditor(editor: Editor | null, tokenName: string) {
  if (!editor) return;
  const { from, to } = editor.state.selection;
  editor
    .chain()
    .focus()
    .insertContentAt(
      { from, to },
      {
        type: 'mergeToken',
        attrs: { name: tokenName },
      }
    )
    .run();
}

/**
 * Reusable helper: inserts or replaces selected text inside a native HTML textarea element
 */
export function insertTextToTextarea(
  textarea: HTMLTextAreaElement | null,
  text: string,
  currentValue: string,
  onChange: (val: string) => void
) {
  if (!textarea) return;
  const start = textarea.selectionStart ?? currentValue.length;
  const end = textarea.selectionEnd ?? currentValue.length;
  const newValue = currentValue.slice(0, start) + text + currentValue.slice(end);
  onChange(newValue);
  const newPos = start + text.length;
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(newPos, newPos);
  });
}
